import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button, Card, KpiCard, Spinner, StatusPill } from '../components/ui';
import { AdminAPI, AuditAPI, ModerationAPI } from '../services/endpoints/admin';
import { MediaAPI } from '../services/endpoints/media';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { AdminMetricsOverview, PlaysPoint, AuditEntry, Comment, FeaturedStatus } from '../types/api';
import { formatCount, formatNaira, formatPct, relativeTime } from '../utils/format';
import './OverviewPage.css';

export function OverviewPage() {
  const nav = useNavigate();
  const [overview, setOverview] = useState<AdminMetricsOverview | null>(null);
  const [plays, setPlays] = useState<PlaysPoint[]>([]);
  const [recent, setRecent] = useState<AuditEntry[]>([]);
  const [flagged, setFlagged] = useState<Comment[]>([]);
  const [health, setHealth] = useState<{ status: string; checks: Record<string, boolean> } | null>(null);
  const [featured, setFeatured] = useState<FeaturedStatus | null>(null);
  const [rotating, setRotating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { run } = useDispatchAction();

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true); setError(null);
      const results = await Promise.allSettled([
        AdminAPI.overview(),
        AdminAPI.plays('30d'),
        AuditAPI.list(0, 12),
        ModerationAPI.comments('FLAGGED', 0, 3),
        AdminAPI.health(),
        MediaAPI.featuredStatus(),
      ]);
      if (!alive) return;
      const [o, p, a, m, h, f] = results;
      if (o.status === 'fulfilled') setOverview(o.value); else setOverview(null);
      if (p.status === 'fulfilled') setPlays(p.value); else setPlays([]);
      if (a.status === 'fulfilled') setRecent(a.value.data || []); else setRecent([]);
      if (m.status === 'fulfilled') setFlagged(m.value.data || []); else setFlagged([]);
      if (h.status === 'fulfilled') setHealth(h.value); else setHealth(null);
      if (f.status === 'fulfilled') setFeatured(f.value); else setFeatured(null);
      if (o.status === 'rejected' && p.status === 'rejected') {
        setError('Admin metrics endpoints are not yet available on this server.');
      }
      setLoading(false);
    }
    load();
    return () => { alive = false; };
  }, []);

  async function rotateNow() {
    setRotating(true);
    const res = await run('Rotate featured', () => MediaAPI.rotateFeatured());
    if (res) setFeatured(res);
    setRotating(false);
  }

  const todayIso = new Date().toISOString().slice(0, 10);
  const subTrend = generateSpark(overview?.subscribers?.active ?? 0, 14);
  // MRR is "not tracked" until a payments-amount source exists; the backend
  // signals this with available=false so we avoid showing a misleading ₦0.
  const mrrAvailable = overview?.mrr?.available !== false;
  const mrrTrend = generateSpark(overview?.mrr?.naira ?? 0, 14);
  const playsTrend = (plays.length ? plays.slice(-14).map(p => p.plays) : generateSpark(overview?.plays?.last24h ?? 0, 14));

  return (
    <div className="ov">
      {/* ── Masthead */}
      <header className="ov__masthead">
        <div className="ov__masthead-left">
          <div className="ov__eyebrow">EDITOR'S DESK · ADMIN · {todayIso}</div>
          <h1 className="ov__title">Today, <em>{formatCount(overview?.plays?.last24h ?? 0)}</em> plays.</h1>
          <p className="ov__lede">
            {overview ? (
              <>That's <em>{formatPct(overview.plays.delta7d)}</em> against last week — {formatCount(overview.subscribers.active)} active subscriptions across {formatCount(overview.subscribers.activeUsers)} subscribers{mrrAvailable ? <>, {formatNaira(overview.mrr.naira, { compact: true })} of monthly recurring revenue,</> : <>,</>} and {overview.moderation.pending} comments awaiting moderation.</>
            ) : (
              <>Refreshed {relativeTime(new Date().toISOString())}. The desk reads quietly today — connect <code>/api/v1/admin/metrics/overview</code> to see traffic.</>
            )}
          </p>
        </div>
        <div className="ov__masthead-right">
          {/* <Button tone="ember" onClick={() => nav('/media')}>Upload media</Button> */}
          <Button tone="ink" onClick={() => nav('/broadcasts')}>Send broadcast</Button>
          <Button onClick={() => nav('/users')}>Gift subscription</Button>
        </div>
      </header>

      {/* ── KPI row */}
      <section className="ov__kpis">
        <KpiCard
          caption="Active subscriptions"
          value={formatCount(overview?.subscribers?.active ?? 0)}
          delta={overview ? { value: formatPct(overview.subscribers.delta7d), direction: deltaDir(overview.subscribers.delta7d) } : undefined}
          trend={subTrend}
        />
        <KpiCard
          caption="Subscribers · active"
          value={formatCount(overview?.subscribers?.activeUsers ?? 0)}
        />
        <KpiCard
          caption="MRR · ₦"
          value={mrrAvailable ? formatNaira(overview?.mrr?.naira ?? 0, { compact: true }) : '—'}
          delta={mrrAvailable && overview ? { value: formatPct(overview.mrr.delta7d), direction: deltaDir(overview.mrr.delta7d) } : undefined}
          trend={mrrAvailable ? mrrTrend : undefined}
          footer={mrrAvailable ? undefined : <StatusPill tone="brass">Not tracked</StatusPill>}
        />
        <KpiCard
          caption="Daily plays · 24h"
          value={formatCount(overview?.plays?.last24h ?? 0)}
          delta={overview ? { value: formatPct(overview.plays.delta7d), direction: deltaDir(overview.plays.delta7d) } : undefined}
          trend={playsTrend}
        />
        <KpiCard
          caption="Pending moderation"
          value={formatCount(overview?.moderation?.pending ?? 0)}
          footer={overview && overview.moderation.pending > 0 ? <StatusPill tone="ember">Needs review</StatusPill> : <StatusPill tone="moss">All clear</StatusPill>}
        />
      </section>

      {/* ── Two-column row */}
      <section className="ov__split">
        <Card>
          <div className="ov__panel-head">
            <div>
              <div className="ov__panel-eyebrow">PLAYS · 30 DAYS</div>
              <div className="ov__panel-title">Daily playback</div>
            </div>
            <div className="ov__panel-meta">{plays.length} samples</div>
          </div>
          <div className="ov__chart">
            {loading ? <ChartSkeleton /> : plays.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={plays} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10, fill: '#6E6357' }}
                    interval={Math.ceil(plays.length / 8)}
                    tickLine={false}
                    axisLine={{ stroke: '#D9CEB4' }}
                  />
                  <YAxis
                    tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10, fill: '#6E6357' }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(217,83,30,0.08)' }}
                    contentStyle={{
                      background: '#FBF7EC',
                      border: '1px solid #C7B997',
                      borderRadius: 2,
                      fontFamily: 'IBM Plex Mono',
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="plays" fill="#D9531E" radius={[1, 1, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="ov__chart-empty">
                <em>No play data yet. Connect <code>/api/v1/admin/metrics/plays</code>.</em>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="ov__panel-head">
            <div>
              <div className="ov__panel-eyebrow">QUEUE · TODAY</div>
              <div className="ov__panel-title">Awaiting review</div>
            </div>
            <button className="ov__panel-link" onClick={() => nav('/moderation')}>View all →</button>
          </div>
          <ul className="ov__queue">
            {flagged.length ? flagged.map(c => (
              <li key={c.id} className="ov__queue-item">
                <div className="ov__queue-meta">
                  <StatusPill tone="ember">Flagged · {c.flagCount ?? 1}</StatusPill>
                  <span className="ov__queue-time">{relativeTime(c.createdOn)}</span>
                </div>
                <p className="ov__queue-content">{c.content}</p>
                <div className="ov__queue-sub">{c.userName || c.userEmail || c.userId} · on {c.discussionId || '—'}</div>
              </li>
            )) : (
              <li className="ov__queue-empty">
                <em>No flagged comments. The room is quiet.</em>
              </li>
            )}
          </ul>
        </Card>
      </section>

      {/* ── Featured rotation */}
      <section>
        <Card>
          <div className="ov__panel-head">
            <div>
              <div className="ov__panel-eyebrow">ROTATION · TODAY</div>
              <div className="ov__panel-title">Featured today</div>
            </div>
            <Button tone="ember" onClick={rotateNow} disabled={rotating || !featured}>
              {rotating ? 'Rotating…' : 'Rotate now'}
            </Button>
          </div>

          {!featured ? (
            <div className="ov__featured-empty">
              <em>Featured status unavailable. Connect <code>/api/media/featured/status</code>.</em>
            </div>
          ) : featured.current ? (
            <div className="ov__featured">
              <div
                className="ov__featured-thumb"
                style={thumbStyle(featured.current.imageBase64Portrait?.url || featured.current.imageBase64Landscape?.url)}
                onClick={() => nav(`/media/${featured.current!.id}`)}
                role="link"
              />
              <div className="ov__featured-body">
                <div className="ov__featured-meta">
                  {featured.pinned
                    ? <StatusPill tone="ember">Pinned · rotation paused</StatusPill>
                    : <StatusPill tone="moss">Auto · rotates daily</StatusPill>}
                  <span className="ov__featured-time">
                    {featured.lastFeaturedAt ? `Last changed ${relativeTime(featured.lastFeaturedAt)}` : 'Not yet rotated'}
                  </span>
                </div>
                <button className="ov__featured-title" onClick={() => nav(`/media/${featured.current!.id}`)}>
                  {featured.current.title || '(untitled)'}
                </button>
                <div className="ov__featured-sub">
                  {featured.current.collectionName || '—'}
                  {featured.current.author ? ` · by ${featured.current.author}` : ''}
                </div>
                <div className="ov__featured-coverage">
                  Coverage: <strong>{formatCount(featured.featuredThisCycle)}</strong> / {formatCount(featured.totalPublished)} featured this cycle
                </div>
              </div>
            </div>
          ) : (
            <div className="ov__featured-empty">
              <em>No featured item yet — the rotation will pick one within the hour, or hit “Rotate now”.</em>
            </div>
          )}
        </Card>
      </section>

      {/* ── Activity + health */}
      <section className="ov__split">
        <Card>
          <div className="ov__panel-head">
            <div>
              <div className="ov__panel-eyebrow">LEDGER · LATEST</div>
              <div className="ov__panel-title">Recent activity</div>
            </div>
            <button className="ov__panel-link" onClick={() => nav('/audit')}>Full audit →</button>
          </div>
          <ol className="ov__ledger">
            {recent.length ? recent.map(e => (
              <li key={e.id} className="ov__ledger-row">
                <span className="ov__ledger-ts">{formatHm(e.timestamp)}</span>
                <span className="ov__ledger-actor">{e.actor}</span>
                <span className="ov__ledger-action">{e.action}</span>
                <span className="ov__ledger-res">{e.resource}{e.resourceId ? ` · ${shortId(e.resourceId)}` : ''}</span>
              </li>
            )) : (
              <li className="ov__ledger-empty"><em>No audit entries recorded yet.</em></li>
            )}
          </ol>
        </Card>

        <Card>
          <div className="ov__panel-head">
            <div>
              <div className="ov__panel-eyebrow">SYSTEM · NOW</div>
              <div className="ov__panel-title">Service health</div>
            </div>
            {health ? <StatusPill tone={health.status === 'ok' ? 'moss' : 'ember'}>{health.status}</StatusPill> : <Spinner />}
          </div>
          <ul className="ov__health">
            {health ? Object.entries(health.checks).map(([k, ok]) => (
              <li key={k} className="ov__health-row">
                <span className="ov__health-dot" data-ok={ok ? '1' : '0'} />
                <span className="ov__health-name">{k}</span>
                <span className="ov__health-state">{ok ? 'OK' : 'DOWN'}</span>
              </li>
            )) : (
              <li className="ov__health-row ov__health-row--empty">
                <em>/api/v1/admin/health not connected.</em>
              </li>
            )}
          </ul>
        </Card>
      </section>

      {error && <div className="ov__error">{error}</div>}
    </div>
  );
}

function thumbStyle(url?: string): CSSProperties {
  return url ? { backgroundImage: `url(${url})` } : {};
}

function deltaDir(n: number | undefined): 'up' | 'down' | 'flat' {
  if (n == null) return 'flat';
  if (n > 0.5) return 'up';
  if (n < -0.5) return 'down';
  return 'flat';
}

function generateSpark(base: number, n: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const noise = (Math.sin(i * 1.3) * 0.15 + Math.cos(i * 0.7) * 0.1);
    out.push(Math.max(0, base * (1 + noise) - i * (base * 0.005)));
  }
  return out.reverse();
}

function formatHm(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toTimeString().slice(0, 5);
}

function shortId(id: string): string {
  if (id.length <= 10) return id;
  return id.slice(0, 6) + '…' + id.slice(-3);
}

function ChartSkeleton() {
  return (
    <div className="ov__skel">
      {Array.from({ length: 30 }).map((_, i) => (
        <span key={i} style={{ height: `${20 + ((i * 13) % 60)}%` }} />
      ))}
    </div>
  );
}
